import API_BASE from '../config';
import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Check, AlertCircle, MessageCircle } from 'lucide-react';

const API_URL = API_BASE;

const colors = {
  primary: '#1A6FD6',
  primaryLight: '#E8F1FC',
  navy: '#0D1F3C',
  body: '#6B7280',
  border: '#E5E9F0',
  surface: '#FFFFFF',
  success: '#16A34A',
  successLight: '#D1FAE5',
  danger: '#DC2626',
  dangerLight: '#FEE2E2',
};

/**
 * Two-step WhatsApp verifier.
 *  Step 1 — phone input + "Send OTP"
 *  Step 2 — OTP input + "Verify"
 *  Step 3 — verified pill + read-only display
 *
 * Props:
 *   onVerified(phoneNormalized, quota) — fired when the user enters a correct OTP
 */
const WhatsAppVerifier = ({ onVerified }) => {
  const [phone, setPhone] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [hint, setHint] = useState('');
  const [verified, setVerified] = useState(false);
  const [normalized, setNormalized] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef(null);

  useEffect(() => () => { if (cooldownRef.current) clearInterval(cooldownRef.current); }, []);

  const startCooldown = (seconds) => {
    if (!seconds) return;
    setCooldown(seconds);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setCooldown(c => {
        if (c <= 1) { clearInterval(cooldownRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  // Mobile browsers occasionally drop fetches transparently (poor signal, in-app webview, etc.).
  // Wrap with a 30-second AbortController and one automatic retry on TypeError.
  const fetchJsonWithRetry = async (url, init, attempts = 2) => {
    let lastErr;
    for (let i = 0; i < attempts; i++) {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 30000);
      try {
        const res = await fetch(url, { ...init, signal: ctrl.signal });
        clearTimeout(t);
        const data = await res.clone().json();
        return data;
      } catch (e) {
        clearTimeout(t);
        lastErr = e;
        // Only retry on network-level failures, not on parse errors
        if (e.name === 'AbortError' || e.name === 'TypeError') {
          if (i < attempts - 1) continue;
        }
        throw e;
      }
    }
    throw lastErr;
  };

  const sendOtp = async () => {
    setError('');
    setHint('');
    setSending(true);
    try {
      const data = await fetchJsonWithRetry(`${API_URL}/api/whatsapp/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      if (!data.success) {
        setError(data.message || 'Could not send OTP');
        if (data.retry_after) startCooldown(data.retry_after);
        return;
      }
      setNormalized(data.phone_normalized || phone);
      setOtpSent(true);
      if (data.mock_mode) {
        setHint('Preview / dev mode — enter 123456 to verify.');
      } else {
        setHint(`We sent a 6-digit code to ${data.phone_normalized}.`);
      }
      startCooldown(30);
    } catch (e) {
      const msg = e.name === 'AbortError'
        ? 'Connection timed out. Check your internet and tap "Send OTP" again.'
        : e.name === 'TypeError'
          ? 'Couldn\'t reach our server. Check your internet and try again.'
          : `Network error: ${e.message || 'Please try again.'}`;
      setError(msg);
    } finally {
      setSending(false);
    }
  };

  const verify = async () => {
    setError('');
    setVerifying(true);
    try {
      const data = await fetchJsonWithRetry(`${API_URL}/api/whatsapp/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, otp }),
      });
      if (!data.success) {
        setError(data.message || 'Invalid OTP');
        return;
      }
      setVerified(true);
      setNormalized(data.phone_normalized);
      if (onVerified) onVerified(data.phone_normalized, data.quota);
    } catch (e) {
      const msg = e.name === 'AbortError'
        ? 'Connection timed out. Check your internet and try again.'
        : e.name === 'TypeError'
          ? 'Couldn\'t reach our server. Check your internet and try again.'
          : `Network error: ${e.message || 'Please try again.'}`;
      setError(msg);
    } finally {
      setVerifying(false);
    }
  };

  const reset = () => {
    setVerified(false);
    setOtpSent(false);
    setOtp('');
    setError('');
    setHint('');
  };

  if (verified) {
    return (
      <div
        className="flex items-center justify-between p-3 rounded-lg border"
        style={{ backgroundColor: colors.successLight, borderColor: colors.success + '40' }}
        data-testid="whatsapp-verified"
      >
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4" style={{ color: colors.success }} />
          <span className="text-sm font-medium" style={{ color: colors.navy }}>
            WhatsApp verified: {normalized}
          </span>
        </div>
        <button
          type="button"
          onClick={reset}
          className="text-xs font-medium underline"
          style={{ color: colors.body }}
          data-testid="whatsapp-change-btn"
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="whatsapp-verifier">
      <label className="block text-sm font-semibold" style={{ color: colors.navy }}>
        WhatsApp number
      </label>
      <p className="text-xs -mt-1" style={{ color: colors.body }}>
        We'll send your evaluation PDF to this number after the report.
      </p>

      {/* Stack vertically on mobile, side-by-side on sm+ — so the CTA is always visible */}
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+91 98765 43210"
          disabled={otpSent}
          className="w-full sm:flex-1 px-4 py-3 rounded-lg border text-base focus:outline-none focus:ring-2 focus:ring-[#1A6FD6] focus:border-transparent disabled:bg-gray-50"
          style={{ borderColor: colors.border, color: colors.navy }}
          data-testid="whatsapp-phone-input"
        />
        {!otpSent ? (
          <button
            type="button"
            onClick={sendOtp}
            disabled={sending || !phone.trim()}
            className="w-full sm:w-auto px-5 py-3 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ backgroundColor: colors.primary, color: '#fff' }}
            data-testid="whatsapp-send-otp-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
            Send OTP
          </button>
        ) : (
          <button
            type="button"
            onClick={sendOtp}
            disabled={cooldown > 0 || sending}
            className="w-full sm:w-auto px-4 py-3 rounded-lg font-medium text-sm disabled:opacity-60"
            style={{ backgroundColor: colors.surface, color: colors.body, border: `1px solid ${colors.border}` }}
            data-testid="whatsapp-resend-btn"
          >
            {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend'}
          </button>
        )}
      </div>

      {otpSent && (
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="6-digit OTP"
            className="w-full sm:flex-1 px-4 py-3 rounded-lg border text-base tracking-widest focus:outline-none focus:ring-2 focus:ring-[#1A6FD6] focus:border-transparent"
            style={{ borderColor: colors.border, color: colors.navy }}
            data-testid="whatsapp-otp-input"
          />
          <button
            type="button"
            onClick={verify}
            disabled={verifying || otp.length !== 6}
            className="w-full sm:w-auto px-6 py-3 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ backgroundColor: colors.primary, color: '#fff' }}
            data-testid="whatsapp-verify-btn"
          >
            {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
          </button>
        </div>
      )}

      {hint && !error && (
        <p className="text-xs" style={{ color: colors.body }} data-testid="whatsapp-hint">
          {hint}
        </p>
      )}

      {error && (
        <div
          className="flex items-start gap-2 p-3 rounded-lg"
          style={{ backgroundColor: colors.dangerLight }}
          data-testid="whatsapp-error"
        >
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: colors.danger }} />
          <span className="text-sm" style={{ color: colors.danger }}>{error}</span>
        </div>
      )}
    </div>
  );
};

export default WhatsAppVerifier;
